import { AddParticipantForm } from './add-participant-form';
import { BulkImportDialog } from './bulk-import-dialog';
import { GenerateTestParticipantsButton } from './generate-test-participants-button';
import { ParticipantsTable } from './participants-table';
import { LoadingState, ErrorState } from '@/components/design';
import { toast } from '@/components/ui/sonner';
import { useParticipants } from '@/data/use-participants';
import type { Participant, Tournament } from '@/data/types';

interface Props {
  tournament: Tournament;
}

function exportText(participants: Participant[]): string {
  return participants
    .map((p) => {
      const clubs = p.players.map((pl) => pl.club).filter(Boolean);
      return p.displayName + (clubs.length ? ', ' + clubs.join(' / ') : '');
    })
    .join('\n');
}

export function ParticipantsTab({ tournament }: Props) {
  const { participants, error } = useParticipants(tournament.id);

  if (error) {
    return <ErrorState body={error.message} />;
  }

  if (participants === undefined) {
    return <LoadingState label="Загрузка участников..." />;
  }

  async function handleExport() {
    if (!participants) return;
    try {
      await navigator.clipboard.writeText(exportText(participants));
      toast.success('Список скопирован');
    } catch {
      toast.error('Не удалось скопировать');
    }
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 360px) 1fr',
        gap: 24,
        alignItems: 'flex-start',
      }}
    >
      {/* Левая колонка */}
      <div style={{ position: 'sticky', top: 80, display: 'grid', gap: 16 }}>
        <div className="card-d card-pad">
          <div className="t-eyebrow" style={{ marginBottom: 12 }}>
            Добавить участника
          </div>
          <AddParticipantForm
            tournamentId={tournament.id}
            discipline={tournament.discipline}
            currentCount={participants.length}
          />
        </div>

        <div className="card-d card-pad">
          <div className="t-eyebrow" style={{ marginBottom: 12 }}>
            Массово
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            <GenerateTestParticipantsButton
              tournamentId={tournament.id}
              discipline={tournament.discipline}
              currentCount={participants.length}
            />
            <BulkImportDialog
              tournamentId={tournament.id}
              discipline={tournament.discipline}
              currentCount={participants.length}
            />
            {participants.length > 0 && (
              <button className="btn" onClick={handleExport}>
                Скопировать списком
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Правая колонка */}
      <div className="card-d" style={{ overflow: 'hidden' }}>
        <div
          className="row"
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            justifyContent: 'space-between',
          }}
        >
          <div className="td-h3">Список участников</div>
          <div className="t-xs t-mute">
            {participants.length} из 128
          </div>
        </div>
        <ParticipantsTable tournamentId={tournament.id} participants={participants} />
      </div>
    </div>
  );
}
